import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Grid, TextField, Typography, Button, Alert,
    CircularProgress, Divider, Paper, Tooltip, MenuItem, Switch,
    ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import LoyaltyIcon from "@mui/icons-material/Loyalty";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { loyaltyApiDetail, loyaltyUserMessage } from "../../utils/loyaltyApiMessage";
import {
    BYPASS_HELP,
    MODE_HELP,
    MODE_SWITCH_CONFIRM,
    QUALIFICATION_MODE,
    bypassErrorFromApi,
    isBaseTier,
    isPurchasesMode,
    normalizeQualificationMode,
    validateBypassConfig,
} from "../../utils/loyaltyQualification";

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const calendarDaysFromToday = (amount, unit) => {
    const n = Math.max(1, Math.floor(Number(amount)));
    if (unit === "days") return n;

    const today = new Date();
    const future = new Date(today);
    if (unit === "years") {
        future.setFullYear(future.getFullYear() + n);
    } else if (unit === "months") {
        future.setMonth(future.getMonth() + n);
    }
    return Math.round((future - today) / (1000 * 60 * 60 * 24));
};

const formatDate = (date) =>
    date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

const sanitizeDaysValue = (value) => {
    if (value == null) return "";
    const raw = String(value).trim();
    if (raw === "") return "";
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return "";
    const floored = Math.floor(numeric);
    if (floored < 0) return "";
    return String(floored);
};

// ── Helper ────────────────────────────────────────────────────────────────────
function SettingField({
    label, helperText, value, onChange, disabled, tooltip,
}) {
    const [amount, setAmount] = useState("");
    const [unit, setUnit] = useState("days");

    useEffect(() => {
        if (value !== "") {
            const currentComputed = amount ? String(calendarDaysFromToday(amount, unit)) : "";
            if (currentComputed !== String(value)) {
                setAmount(String(value));
                setUnit("days");
            }
        } else if (amount !== "") {
            setAmount("");
        }
    }, [value]);

    const triggerChange = (newAmount, newUnit) => {
        if (!newAmount) {
            onChange({ target: { value: "" } });
        } else {
            onChange({ target: { value: String(calendarDaysFromToday(newAmount, newUnit)) } });
        }
    };

    const handleAmountChange = (e) => {
        const newAmount = sanitizeDaysValue(e.target.value);
        setAmount(newAmount);
        triggerChange(newAmount, unit);
    };

    const handleUnitChange = (e) => {
        const newUnit = e.target.value;
        setUnit(newUnit);
        triggerChange(amount, newUnit);
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
                <Typography fontSize={13} fontWeight={600} color="#1a1a2e">
                    {label}
                </Typography>
                {tooltip && (
                    <Tooltip title={tooltip} placement="top" arrow>
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: "#94a3b8", cursor: "help" }} />
                    </Tooltip>
                )}
            </Box>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Quantité"
                        placeholder="Illimité (laisser vide)"
                        value={amount}
                        disabled={disabled}
                        onChange={handleAmountChange}
                        InputProps={{ inputProps: { min: 0 } }}
                        sx={inputSx}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        select
                        size="small"
                        label="Unité"
                        value={unit}
                        disabled={disabled}
                        onChange={handleUnitChange}
                        sx={inputSx}
                    >
                        <MenuItem value="days">Jours</MenuItem>
                        <MenuItem value="months">Mois</MenuItem>
                        <MenuItem value="years">Années</MenuItem>
                    </TextField>
                </Grid>
            </Grid>
            <Typography fontSize={11} color="text.secondary" mt={0.5}>{helperText}</Typography>
        </Box>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LoyaltySettingsPanel({ brandName, onNotify }) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // form state — empty string means "illimité" (null in backend)
    const [form, setForm] = useState({
        qualification_mode: QUALIFICATION_MODE.POINTS,
        points_validity_days: "",
        loyalty_status_validity_days: "",
        purchase_validity_days: "",
        first_purchase_bypass_enabled: false,
        first_purchase_bypass_min_amount: "",
        first_purchase_bypass_tier_key: "",
    });
    const [original, setOriginal] = useState(null);
    const [tiers, setTiers] = useState([]);
    const [modeConfirm, setModeConfirm] = useState(null);
    const [uiError, setUiError] = useState(null);

    const mapSettings = (data = {}) => ({
        qualification_mode: normalizeQualificationMode(data.qualification_mode),
        points_validity_days: sanitizeDaysValue(data.points_validity_days),
        loyalty_status_validity_days: sanitizeDaysValue(data.loyalty_status_validity_days),
        purchase_validity_days: sanitizeDaysValue(data.purchase_validity_days),
        first_purchase_bypass_enabled: Boolean(data.first_purchase_bypass_enabled),
        first_purchase_bypass_min_amount: data.first_purchase_bypass_min_amount ?? "",
        first_purchase_bypass_tier_key: data.first_purchase_bypass_tier_key || "",
    });

    // ── Load current settings ─────────────────────────────────────────────────
    const loadSettings = useCallback(async () => {
        if (!brandName) return;
        setLoading(true);
        setError(null);
        try {
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-settings`, {
                headers: { "X-Brand": brandName },
            });
            if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
            const data = await res.json();
            const next = mapSettings(data);
            setForm(next);
            setOriginal(next);
            const tiersRes = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-tiers`, {
                headers: { "X-Brand": brandName },
            });
            if (tiersRes.ok) {
                const list = await tiersRes.json();
                setTiers(Array.isArray(list) ? list : []);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [brandName]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const isDirty = original !== null && JSON.stringify(form) !== JSON.stringify(original);

    const set = (field) => (e) => setForm((p) => ({ ...p, [field]: sanitizeDaysValue(e.target.value) }));

    const activeBypassTiers = tiers.filter((t) => t.active && !isBaseTier(t));

    const requestModeChange = (_e, nextMode) => {
        if (!nextMode || nextMode === form.qualification_mode) return;
        if (original && nextMode !== original.qualification_mode) {
            setModeConfirm(nextMode);
            return;
        }
        setForm((p) => ({ ...p, qualification_mode: nextMode }));
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        const bypassCheck = validateBypassConfig({
            enabled: form.first_purchase_bypass_enabled,
            minAmount: form.first_purchase_bypass_min_amount,
            tierKey: form.first_purchase_bypass_tier_key,
        });
        if (!bypassCheck.valid) {
            setUiError(bypassCheck.error);
            return;
        }
        setSaving(true);
        setError(null);
        setUiError(null);
        try {
            const daysOrNull = (v) => {
                const s = sanitizeDaysValue(v);
                return s !== "" ? Number(s) : null;
            };
            const amount = form.first_purchase_bypass_enabled
                ? Number(form.first_purchase_bypass_min_amount)
                : (form.first_purchase_bypass_min_amount === "" ? null : Number(form.first_purchase_bypass_min_amount));
            const body = {
                qualification_mode: form.qualification_mode,
                points_validity_days: daysOrNull(form.points_validity_days),
                loyalty_status_validity_days: daysOrNull(form.loyalty_status_validity_days),
                purchase_validity_days: daysOrNull(form.purchase_validity_days),
                first_purchase_bypass_enabled: Boolean(form.first_purchase_bypass_enabled),
                first_purchase_bypass_min_amount: form.first_purchase_bypass_enabled
                    ? amount
                    : (Number.isFinite(amount) ? amount : null),
                first_purchase_bypass_tier_key: form.first_purchase_bypass_enabled
                    ? form.first_purchase_bypass_tier_key
                    : (form.first_purchase_bypass_tier_key || null),
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-settings`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": brandName,
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                const detail = loyaltyApiDetail(e?.detail) || `Erreur HTTP ${res.status}`;
                throw new Error(bypassErrorFromApi(detail) || loyaltyUserMessage(e?.detail, detail));
            }
            const data = await res.json();
            const next = mapSettings(data);
            setForm(next);
            setOriginal(next);
            onNotify?.("Paramètres de fidélité mis à jour", "success");
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    if (!brandName) {
        return (
            <Box py={4} textAlign="center">
                <Typography color="text.secondary" fontSize={13}>
                    Sélectionnez une marque pour accéder aux paramètres.
                </Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box py={5} display="flex" justifyContent="center">
                <CircularProgress size={28} sx={{ color: SECTION_COLORS.rewards.main }} />
            </Box>
        );
    }

    const purchasesMode = isPurchasesMode(form.qualification_mode);

    return (
        <Box width="100%">

            <Paper
                elevation={0}
                sx={{
                    mb: 3, p: 3, borderRadius: 3,
                    border: "1px solid #e8ecf1",
                    background: "linear-gradient(135deg, #f8fbff 0%, #fff 100%)",
                    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                }}
            >
                <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e" mb={0.5}>
                    Mode de qualification
                </Typography>
                <Typography fontSize={12} color="text.secondary" mb={2}>
                    {MODE_HELP[form.qualification_mode]}
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    value={form.qualification_mode}
                    onChange={requestModeChange}
                    disabled={saving}
                    sx={{ mb: 1 }}
                >
                    <ToggleButton value={QUALIFICATION_MODE.POINTS} sx={{ textTransform: "none", fontWeight: 700, px: 2 }}>
                        Points
                    </ToggleButton>
                    <ToggleButton value={QUALIFICATION_MODE.PURCHASES} sx={{ textTransform: "none", fontWeight: 700, px: 2 }}>
                        Achats
                    </ToggleButton>
                </ToggleButtonGroup>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    mb: 3, p: 3, borderRadius: 3,
                    border: "1px solid #e8ecf1",
                    background: "linear-gradient(135deg, #fff7ed 0%, #fff 100%)",
                    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1}>
                    <Box>
                        <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e">
                            Bypass premier achat
                        </Typography>
                        <Typography fontSize={12} color="text.secondary" mt={0.5} maxWidth={640}>
                            {BYPASS_HELP}
                        </Typography>
                    </Box>
                    <Switch
                        checked={Boolean(form.first_purchase_bypass_enabled)}
                        disabled={saving}
                        onChange={(e) => setForm((p) => ({ ...p, first_purchase_bypass_enabled: e.target.checked }))}
                    />
                </Box>
                {form.first_purchase_bypass_enabled && (
                    <Grid container spacing={2} mt={0.5}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                type="number"
                                label="Montant minimum"
                                value={form.first_purchase_bypass_min_amount}
                                onChange={(e) => setForm((p) => ({ ...p, first_purchase_bypass_min_amount: e.target.value }))}
                                InputProps={{ inputProps: { min: 1 } }}
                                helperText="Montant du tout premier sale (≥ 1)"
                                sx={inputSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                select
                                size="small"
                                label="Palier cible"
                                value={form.first_purchase_bypass_tier_key}
                                onChange={(e) => setForm((p) => ({ ...p, first_purchase_bypass_tier_key: e.target.value }))}
                                helperText="Paliers actifs uniquement (hors palier de base)"
                                sx={inputSx}
                            >
                                {activeBypassTiers.length === 0 && (
                                    <MenuItem value="" disabled>Aucun palier actif éligible</MenuItem>
                                )}
                                {activeBypassTiers.map((tier) => (
                                    <MenuItem key={tier.key} value={tier.key}>{tier.name} ({tier.key})</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                )}
            </Paper>

            {/* ── Section: Validité des Points ─────────────────────────────── */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3, p: 3, borderRadius: 3,
                    border: "1px solid #e8ecf1",
                    background: "linear-gradient(135deg, #f8fbff 0%, #fff 100%)",
                    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                    },
                }}
            >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Box
                        sx={{
                            width: 36, height: 36, borderRadius: 2,
                            background: SECTION_COLORS.rewards.gradient,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            boxShadow: `0 4px 12px ${SECTION_COLORS.rewards.main}30`,
                        }}
                    >
                        <LoyaltyIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </Box>
                    <Box>
                        <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e">
                            Validité des Points{purchasesMode ? " (secondaire)" : ""}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">
                            Durée au-delà de laquelle les points expirent automatiquement
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <SettingField
                            label="Expiration des points"
                            helperText="Laisser vide = points n'expirent jamais (illimité)."
                            tooltip="Durée au-delà de laquelle un point devient invalide."
                            value={form.points_validity_days}
                            onChange={set("points_validity_days")}
                            disabled={saving}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Box
                            sx={{
                                height: "100%", minHeight: 80, borderRadius: 2,
                                bgcolor: sanitizeDaysValue(form.points_validity_days) !== "" ? "#f0fdf4" : "#f8fafc",
                                border: `1px solid ${sanitizeDaysValue(form.points_validity_days) !== "" ? "#86efac" : "#e2e8f0"}`,
                                display: "flex", flexDirection: "column",
                                alignItems: "center", justifyContent: "center", p: 1.5, gap: 0.5,
                            }}
                        >
                            {sanitizeDaysValue(form.points_validity_days) !== "" ? (
                                <>
                                    <Typography fontSize={22} fontWeight={800} color="#16a34a" fontFamily="Montserrat, sans-serif">
                                        {sanitizeDaysValue(form.points_validity_days)}j
                                    </Typography>
                                    <Typography fontSize={10} color="#16a34a" fontWeight={600}>CONFIGURÉ</Typography>
                                </>
                            ) : (
                                <>
                                    <AllInclusiveIcon sx={{ color: "#94a3b8", fontSize: 28 }} />
                                    <Typography fontSize={10} color="#94a3b8" fontWeight={600}>ILLIMITÉ</Typography>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    mb: 3, p: 3, borderRadius: 3,
                    border: purchasesMode ? "1px solid #fdba74" : "1px solid #e8ecf1",
                    background: "linear-gradient(135deg, #fff7ed 0%, #fff 100%)",
                    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                }}
            >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Box>
                        <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e">
                            Validité des achats{purchasesMode ? "" : " (secondaire)"}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">
                            Expiration des lots d’achats et de montants
                        </Typography>
                    </Box>
                </Box>
                <Divider sx={{ mb: 2.5 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <SettingField
                            label="Expiration des achats"
                            helperText="Laisser vide = les lots d’achats n’expirent jamais."
                            tooltip="Durée au-delà de laquelle un lot d’achats / montant devient invalide."
                            value={form.purchase_validity_days}
                            onChange={set("purchase_validity_days")}
                            disabled={saving}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Box
                            sx={{
                                height: "100%", minHeight: 80, borderRadius: 2,
                                bgcolor: sanitizeDaysValue(form.purchase_validity_days) !== "" ? "#fff7ed" : "#f8fafc",
                                border: `1px solid ${sanitizeDaysValue(form.purchase_validity_days) !== "" ? "#fdba74" : "#e2e8f0"}`,
                                display: "flex", flexDirection: "column",
                                alignItems: "center", justifyContent: "center", p: 1.5, gap: 0.5,
                            }}
                        >
                            {sanitizeDaysValue(form.purchase_validity_days) !== "" ? (
                                <>
                                    <Typography fontSize={22} fontWeight={800} color="#ea580c" fontFamily="Montserrat, sans-serif">
                                        {sanitizeDaysValue(form.purchase_validity_days)}j
                                    </Typography>
                                    <Typography fontSize={10} color="#ea580c" fontWeight={600}>CONFIGURÉ</Typography>
                                </>
                            ) : (
                                <>
                                    <AllInclusiveIcon sx={{ color: "#94a3b8", fontSize: 28 }} />
                                    <Typography fontSize={10} color="#94a3b8" fontWeight={600}>ILLIMITÉ</Typography>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* ── Section: Validité du Statut Fidélité ────────────────────── */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3, p: 3, borderRadius: 3,
                    border: "1px solid #e8ecf1",
                    background: "linear-gradient(135deg, #f8fbff 0%, #fff 100%)",
                    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                    },
                }}
            >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Box
                        sx={{
                            width: 36, height: 36, borderRadius: 2,
                            background: SECTION_COLORS.tiers.gradient,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            boxShadow: `0 4px 12px ${SECTION_COLORS.tiers.main}30`,
                        }}
                    >
                        <LoyaltyIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </Box>
                    <Box>
                        <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e">
                            Validité du Statut de Fidélité
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">
                            Durée de conservation du niveau (tier) d'un client sans activité
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <SettingField
                            label="Durée de validité du statut"
                            helperText="Laisser vide = le statut ne rétrograde jamais automatiquement."
                            tooltip="Durée d'inactivité au-delà de laquelle le statut fidélité peut être rétrogradé."
                            value={form.loyalty_status_validity_days}
                            onChange={set("loyalty_status_validity_days")}
                            disabled={saving}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Box
                            sx={{
                                height: "100%", minHeight: 80, borderRadius: 2,
                                bgcolor: sanitizeDaysValue(form.loyalty_status_validity_days) !== "" ? "#eff6ff" : "#f8fafc",
                                border: `1px solid ${sanitizeDaysValue(form.loyalty_status_validity_days) !== "" ? "#93c5fd" : "#e2e8f0"}`,
                                display: "flex", flexDirection: "column",
                                alignItems: "center", justifyContent: "center", p: 1.5, gap: 0.5,
                            }}
                        >
                            {sanitizeDaysValue(form.loyalty_status_validity_days) !== "" ? (
                                <>
                                    <Typography fontSize={22} fontWeight={800} color="#2563eb" fontFamily="Montserrat, sans-serif">
                                        {sanitizeDaysValue(form.loyalty_status_validity_days)}j
                                    </Typography>
                                    <Typography fontSize={10} color="#2563eb" fontWeight={600}>CONFIGURÉ</Typography>
                                </>
                            ) : (
                                <>
                                    <AllInclusiveIcon sx={{ color: "#94a3b8", fontSize: 28 }} />
                                    <Typography fontSize={10} color="#94a3b8" fontWeight={600}>ILLIMITÉ</Typography>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* ── Alerts ───────────────────────────────────────────────────── */}
            {(error || uiError) && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => { setError(null); setUiError(null); }}>
                    {uiError || error}
                </Alert>
            )}

            {/* ── Save Button ───────────────────────────────────────────────── */}
            <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button
                    variant="outlined"
                    onClick={loadSettings}
                    disabled={saving || loading}
                    sx={{
                        textTransform: "none", fontWeight: 600, borderRadius: 2,
                        fontFamily: "Montserrat, sans-serif",
                        borderColor: "#e2e8f0", color: "#64748b",
                        "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
                    }}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isDirty || saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{
                        textTransform: "none", fontWeight: 700, borderRadius: 2,
                        fontFamily: "Montserrat, sans-serif",
                        background: isDirty ? SECTION_COLORS.rewards.gradient : undefined,
                        boxShadow: isDirty ? `0 4px 12px ${SECTION_COLORS.rewards.main}30` : undefined,
                        "&:hover": { boxShadow: `0 6px 20px ${SECTION_COLORS.rewards.main}40` },
                        px: 3,
                    }}
                >
                    {saving ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </Box>

            <Dialog open={Boolean(modeConfirm)} onClose={() => setModeConfirm(null)}>
                <DialogTitle>Changer le mode de qualification ?</DialogTitle>
                <DialogContent>
                    <DialogContentText>{MODE_SWITCH_CONFIRM}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModeConfirm(null)} sx={{ textTransform: "none" }}>Annuler</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setForm((p) => ({ ...p, qualification_mode: modeConfirm }));
                            setModeConfirm(null);
                        }}
                        sx={{ textTransform: "none" }}
                    >
                        Confirmer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
